import React from 'react';
import { Close as XIcon, DeleteOutline as DeleteIcon, Settings as Settings2Icon } from '@mui/icons-material';
import { T, fontDisplay, fontBody } from '../tokens';
import ValidationMessages from '../templates/ValidationMessages';
import ReplyNodeEditor from './editors/ReplyNodeEditor';
import DmNodeEditor from './editors/DmNodeEditor';
import LeadNodeEditor from './editors/LeadNodeEditor';
import KeywordNodeEditor from './editors/KeywordNodeEditor';
import AssignNodeEditor from './editors/AssignNodeEditor';
import AutomationMetaEditor from './editors/AutomationMetaEditor';
import VaanikNodeEditor from './editors/VaanikNodeEditor';
import ToggleRow from '../ToggleRow';

export default function NodeEditorPanel({
  node,
  draft,
  isNew,
  onClose,
  onBodyChange,
  onDmBodyChange,
  onDraftChange,
  onSettingsChange,
  settings,
  errors = {},
  onSaveStep,
  stepSaved,
  onRemoveStep,
  canRemoveStep,
  removeBlockReason,
}) {
  const isCustom = draft.templateType === 'custom';
  if (!node) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: '100%', color: T.slateLight, fontFamily: fontBody, fontSize: 13, textAlign: 'center', padding: 24,
      }}
      >
        <Settings2Icon sx={{ fontSize: 22, mb: 1.25, color: T.slateLight }} />
        Select a step to edit its settings.
      </div>
    );
  }

  const isCommentCustom = isCustom && draft.triggerChannel === 'instagram_comment';

  return (
    <div className="sma-fade-in" style={{ padding: 20, display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontFamily: fontDisplay, fontSize: 15, fontWeight: 600, color: T.ink }}>{node.label}</span>
        <button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
          <XIcon sx={{ fontSize: 16, color: T.slateLight }} />
        </button>
      </div>

      {node.kind === 'trigger' && (
        <AutomationMetaEditor
          name={draft.name}
          triggerLibraryId={draft.triggerLibraryId}
          status={draft.status}
          createLead={isCustom ? draft.createLead : undefined}
          isChannelDefault={draft.isChannelDefault}
          onNameChange={(name) => onDraftChange({ name })}
          onTriggerLibraryChange={(triggerLibraryId) => onDraftChange({ triggerLibraryId })}
          onStatusChange={(status) => onDraftChange({ status })}
          onCreateLeadChange={isCustom ? (on) => onDraftChange({ createLead: on }) : undefined}
          onIsChannelDefaultChange={(on) => onDraftChange({ isChannelDefault: on })}
          errors={errors}
          isNew={isNew}
        />
      )}

      {node.id === 'keyword' && (
        <KeywordNodeEditor
          keywords={draft.keywords}
          matchRule={draft.matchRule}
          onKeywordsChange={(keywords) => onDraftChange({ keywords })}
          onMatchRuleChange={(matchRule) => onDraftChange({ matchRule })}
          error={errors.keywords}
        />
      )}

      {node.id === 'reply' && (
        <ReplyNodeEditor
          body={draft.body}
          onBodyChange={onBodyChange}
          error={errors.body}
        />
      )}

      {node.id === 'dm' && (() => {
        // Use dmBody whenever the template has a separate "Reply to Comment" step
        // (comment_reply non-custom OR custom instagram_comment).
        const usesDmBody = draft.templateType === 'comment_reply' || isCommentCustom;
        return (
          <DmNodeEditor
            body={usesDmBody ? draft.dmBody : draft.body}
            onBodyChange={usesDmBody ? onDmBodyChange : onBodyChange}
            error={errors.body}
            label={
              draft.templateType === 'comment_reply' || isCommentCustom
                ? 'Direct message text'
                : draft.templateType === 'dm_reply'
                  ? 'Incoming DM auto-reply'
                  : 'Direct message'
            }
          />
        );
      })()}

      {node.id === 'lead' && isCustom && (
        <ToggleRow
          label="Create CRM lead on trigger"
          checked={!!draft.createLead}
          onChange={(on) => onDraftChange({ createLead: on })}
        />
      )}

      {node.id === 'lead' && !isCustom && (
        <LeadNodeEditor
          templateType={draft.templateType}
          settings={settings}
          onSettingsChange={onSettingsChange}
        />
      )}

      {node.id === 'assign' && (
        <AssignNodeEditor
          assignAgent={draft.assignAgent}
          onAssignAgentChange={(assignAgent) => onDraftChange({ assignAgent })}
        />
      )}

      {node.id === 'vaanik' && (
        <VaanikNodeEditor
          enabled={!!draft.enableVaanikAgent}
          onToggle={(on) => onDraftChange({ enableVaanikAgent: on })}
        />
      )}

      <ValidationMessages errors={errors} />

      <div style={{ marginTop: 'auto', paddingTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {canRemoveStep ? (
          <button
            type="button"
            onClick={onRemoveStep}
            style={{
              width: '100%',
              padding: '9px 0',
              borderRadius: 9,
              background: 'transparent',
              color: '#DC2626',
              border: `1px solid #FECACA`,
              fontFamily: fontBody,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <DeleteIcon sx={{ fontSize: 15 }} /> Remove step
          </button>
        ) : removeBlockReason && (
          <div style={{
            fontFamily: fontBody,
            fontSize: 11.5,
            color: T.slateLight,
            textAlign: 'center',
            lineHeight: 1.45,
            padding: '0 4px',
          }}
          >
            {removeBlockReason}
          </div>
        )}

        <button
          type="button"
          onClick={onSaveStep}
          style={{
            width: '100%',
            padding: '10px 0',
            borderRadius: 9,
            background: T.ink,
            color: '#fff',
            border: 'none',
            fontFamily: fontBody,
            fontWeight: 600,
            fontSize: 13.5,
            cursor: 'pointer',
          }}
        >
          {stepSaved ? 'Step saved' : 'Save step'}
        </button>
      </div>
    </div>
  );
}
